import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { StorageAddonDialog } from "@/components/billing/StorageAddonDialog";
import {
  formatAddonPackLabel,
  formatAddonPriceLabel,
} from "@/lib/billingPlanDisplay";
import { billingService } from "@/services/billingService";
import type { BillingAddonCatalogItem, BillingSummary } from "@/types/billing";

type Props = {
  summary: BillingSummary;
};

const formatRenewalDate = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleDateString();
};

const packLabel = (pack: BillingAddonCatalogItem): string =>
  formatAddonPackLabel(pack);

const grantLabel = (pack: BillingAddonCatalogItem): string | null => {
  const credits = Number(pack.grant_credits);
  if (credits > 0) {
    return `${credits.toLocaleString()} credits`;
  }
  const storage = Number(pack.grant_storage_gb);
  if (storage > 0) {
    return `${storage.toLocaleString()} GB`;
  }
  return null;
};

export const BillingAddonsCard = ({ summary }: Props) => {
  const queryClient = useQueryClient();
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);

  const subscriptionActive =
    summary.status === "active" && summary.subscription_status === "active";

  const catalogQuery = useQuery({
    queryKey: ["billing", "addon-catalog"],
    queryFn: () => billingService.getAddonCatalog(),
  });
  const creditPacks = catalogQuery.data?.credit_packs ?? [];
  const storagePacks = catalogQuery.data?.storage_packs ?? [];

  const storageQuery = useQuery({
    queryKey: ["billing", "storage-addons"],
    queryFn: () => billingService.getStorageAddons(),
    enabled: storagePacks.length > 0,
  });

  const creditMutation = useMutation({
    mutationFn: billingService.createCreditAddonCheckout,
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
    onError: () => {
      toast.error("Could not start the credit purchase", {
        description: "Please try again, or contact support if it continues.",
      });
    },
    onSettled: () => setPendingPriceId(null),
  });

  const storageMutation = useMutation({
    mutationFn: billingService.purchaseStorageAddon,
    onSuccess: (data) => {
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
        return;
      }
      toast.success("Invoice raised", {
        description: `${data.grant_storage_gb} GB is added once the invoice is paid. Find it under Invoices.`,
      });
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
    onError: () => {
      toast.error("Could not raise the invoice", {
        description: "Please try again, or contact support if it continues.",
      });
    },
    onSettled: () => setPendingPriceId(null),
  });

  const removeMutation = useMutation({
    mutationFn: billingService.removeAllStorageAddons,
    onSuccess: (data) => {
      toast.success("Storage add-ons removed", {
        description: `${data.removed_items} removed. No refund is issued for the remaining days.`,
      });
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
    onError: () => {
      toast.error("Could not remove storage add-ons");
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: billingService.removeStorageAddon,
    onSuccess: () => {
      toast.success("Storage add-on removed", {
        description: "No refund is issued for the remaining days.",
      });
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) {
        toast.error("Too much data stored", {
          description:
            "Delete files until they fit the reduced limit, then try again.",
        });
        return;
      }
      toast.error("Could not remove this storage add-on");
    },
  });

  if (catalogQuery.isLoading) {
    return null;
  }

  if (creditPacks.length === 0 && storagePacks.length === 0) {
    return null;
  }

  const renewalDate = formatRenewalDate(summary.current_period_end);
  const activeStorage = storageQuery.data?.storage_addon_gb ?? 0;
  const hasStorageAddons = Number(activeStorage) > 0;
  const storageItems = storageQuery.data?.items ?? [];
  const busy = creditMutation.isPending || storageMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add-ons</CardTitle>
        <CardDescription>
          Extra credits and storage for the current subscription.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!subscriptionActive ? (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            Add-ons are available while your subscription is active and paid up.
          </div>
        ) : null}

        {creditPacks.length > 0 ? (
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-medium">Credit packs</h3>
              <p className="text-sm text-muted-foreground">
                {renewalDate
                  ? `Purchased credits expire with your subscription cycle on ${renewalDate}.`
                  : "Purchased credits expire with your subscription cycle."}
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {creditPacks.map((pack) => (
                <Button
                  key={pack.price_id}
                  variant="outline"
                  className="h-auto w-full flex-col items-start justify-start gap-1 whitespace-normal py-3 text-left"
                  disabled={!subscriptionActive || busy}
                  onClick={() => {
                    setPendingPriceId(pack.price_id);
                    creditMutation.mutate({ price_id: pack.price_id });
                  }}
                >
                  <span>
                    {pendingPriceId === pack.price_id && creditMutation.isPending
                      ? "Opening checkout…"
                      : packLabel(pack)}
                  </span>
                  {grantLabel(pack) ? (
                    <span className="text-xs font-normal text-muted-foreground">
                      {grantLabel(pack)}
                    </span>
                  ) : null}
                </Button>
              ))}
            </div>
          </section>
        ) : null}

        {storagePacks.length > 0 ? (
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-medium">Storage</h3>
              <p className="text-sm text-muted-foreground">
                {hasStorageAddons
                  ? `${activeStorage} GB of add-on storage is active.`
                  : "No add-on storage is active."}
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {storagePacks.map((pack) => (
                <StorageAddonDialog
                  key={pack.price_id}
                  pack={pack}
                  disabled={!subscriptionActive || busy}
                  busyLabel={
                    pendingPriceId === pack.price_id && storageMutation.isPending
                      ? "Preparing invoice…"
                      : null
                  }
                  onConfirm={() => {
                    setPendingPriceId(pack.price_id);
                    storageMutation.mutate({ price_id: pack.price_id });
                  }}
                />
              ))}
            </div>

            {storageItems.length > 0 ? (
              <ul className="divide-y rounded-lg border">
                {storageItems.map((item) => (
                  <li
                    key={item.subscription_item_id}
                    className="flex items-center justify-between gap-3 p-3"
                  >
                    <div className="text-sm">
                      <span className="font-medium">
                        {item.grant_storage_gb} GB
                      </span>
                      {item.status === "pending" ? (
                        <span className="ml-2 text-muted-foreground">
                          awaiting payment
                        </span>
                      ) : null}
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          disabled={removeItemMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Cancel this storage add-on?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {item.grant_storage_gb} GB is removed from your
                            limit immediately and the remaining days of this
                            period are not refunded. Your other add-ons stay.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep it</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() =>
                              removeItemMutation.mutate(
                                item.subscription_item_id
                              )
                            }
                          >
                            Cancel add-on
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </li>
                ))}
              </ul>
            ) : null}

            {hasStorageAddons ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-destructive hover:bg-destructive/10"
                    disabled={removeMutation.isPending}
                  >
                    Remove all storage add-ons
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove storage add-ons?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Your storage limit drops immediately and the remaining
                      days of this period are not refunded. Uploads above the
                      reduced limit will be blocked.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep storage</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => removeMutation.mutate()}
                    >
                      Remove storage
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : null}
          </section>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default BillingAddonsCard;
